import { useCallback, useMemo, type ReactElement } from 'react'
import ReactDOMServer from 'react-dom/server'

import { useCommonServices } from '@lib/hooks'
import { useCtaTracking } from '@lib/hooks/use-cta-tracking'
import { composeUrl } from '@lib/utils/url'

import type { TypeComponentLinkEntry } from '@contentful-types/component-link'

import type { CtaProps } from '@components/cta/cta-prop-types'
import { Button } from '@components/ui-core/button/button'

export type LinkButtonProps = CtaProps & {
  targetLink: TypeComponentLinkEntry
}

export function LinkButton(data: LinkButtonProps): ReactElement {
  const { category = 'primary', targetLink, ...restProps } = data
  const { targetUrl, appendCurrentQueryParams } = targetLink.fields
  const { trackAndNavigate } = useCtaTracking({ placement: 'Section' })
  const { navigationService } = useCommonServices()

  // Compose the final URL with query params
  const finalUrl = useMemo(() => {
    return appendCurrentQueryParams
      ? composeUrl(targetUrl, navigationService.currentQueryParams)
      : targetUrl
  }, [targetUrl, appendCurrentQueryParams, navigationService.currentQueryParams])

  const onClick = useCallback(async () => {
    trackAndNavigate(
      'Link',
      ReactDOMServer.renderToString(restProps.text),
      category === 'unstyled' ? 'text' : category,
      finalUrl,
    )
  }, [finalUrl, restProps.text, category, trackAndNavigate])

  return <Button onClick={onClick} href={finalUrl} category={category} {...restProps} />
}
